import { Modal, Tooltip } from "antd";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";

import {
  type AiQuotaPaywallReason,
  NotificationPosition,
} from "@superblocksteam/shared";

import { useFeatureFlag } from "hooks/ui";
import { getCurrentUser } from "legacy/selectors/usersSelectors";
import { Flag } from "store/slices/featureFlags";
import { selectOnlyOrganization } from "store/slices/organizations";
import {
  type BillingInterval,
  useChangeIntervalMutation,
  useGetAutoTopupConfigQuery,
  useGetPlanSummaryQuery,
  useCreatePortalSessionMutation,
  useManualTopupChargeMutation,
  usePreviewInvoiceMutation,
  useUpdateSubscriptionMutation,
} from "store/slices/reduxApi/billing";
import { useListBuilderSeatsQuery } from "store/slices/reduxApi/builderSeats";
import {
  sendErrorUINotification,
  sendSuccessUINotification,
} from "utils/notification";

import { ReactComponent as FolderUsersIcon } from "../../assets/icons/common/folder-users.svg";
import { AssignBuilderSeatsModal } from "./AssignBuilderSeatsModal";
// AutoTopUpPanel was previously mounted inline here; the bar has been
// retired and top up UI lives at /spending. We keep the cache-key import
// so the Checkout button can still observe in-flight manual top up
// charges from any other surface.
import { MANUAL_TOPUP_CACHE_KEY } from "./AutoTopUpPanel";
import { ContactAdminBillingModal } from "./ContactAdminBillingModal";
import { ManagePlanCreditPanel } from "./ManagePlanCreditPanel";
import { ManagePlanDeployPanel } from "./ManagePlanDeployPanel";
import { ManagePlanSeatPanel } from "./ManagePlanSeatPanel";
import { AI_CREDIT_PACK_OPTIONS } from "./mockData";
import { TopUpView } from "./TopUpView";

export type ManagePlanFocusPanel = "seats" | "credits" | "deployed_apps";

// Fallback deployed-app unit prices used only when `planSummary` is loading
// or the server returns `null` prices (trial/enterprise/pre-versioning orgs).
// These match the previous hardcoded mock so behavior is unchanged while the
// real Stripe prices load.
const FALLBACK_DEPLOYED_APP_PRICE_MONTHLY_USD = 100;
const FALLBACK_DEPLOYED_APP_PRICE_ANNUAL_USD = 1200;

type IntervalSwitchStage = "idle" | "confirm" | "cancel-confirm";

type ManagePlanModalProps = {
  open: boolean;
  onClose: () => void;
  billingInterval?: BillingInterval;
  canManageBilling?: boolean;
  reason?: AiQuotaPaywallReason;
  /** When set, only the specified panel is shown instead of all three. */
  focusPanel?: ManagePlanFocusPanel;
  /** Called when user successfully completes an action (e.g. assigns seat). Use to navigate back to app instead of onClose. */
  onSuccess?: () => void;
  /**
   * Page-level entry points (e.g. UsagePlanBanner's "Switch to annual" /
   * "Cancel switch" buttons) can open the modal pre-set to the confirm or
   * cancel-confirm view instead of the default idle landing page. Applied
   * each time `open` flips true so reopening always honors the caller's
   * intent. Falls back to "idle".
   */
  initialIntervalSwitchStage?: IntervalSwitchStage;
};

function formatUsd(amount: number, decimals = 0) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(amount);
}

function formatResetDate(expiryDate: Date | string | undefined) {
  if (!expiryDate) return null;
  const date = new Date(expiryDate);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

const REASON_BANNER_CONFIG: Partial<
  Record<AiQuotaPaywallReason, { headline: string; cta: string }>
> = {
  credit_limit_exceeded: {
    headline: "You've used all your AI Credits",
    cta: "Purchase a credit pack below to keep building.",
  },
  token_limit_exceeded: {
    headline: "You've used all your AI Credits",
    cta: "Purchase a credit pack below to keep building.",
  },
  no_seat_assigned: {
    headline: "You don't have an AI Builder license",
    cta: "Add a seat below to start building with Clark AI.",
  },
  deploy_quota_exceeded: {
    headline: "You've reached your deployed app limit",
    cta: "Add additional deployed apps below to keep deploying.",
  },
};

function getSubscriptionUpdateErrorDetail(error: unknown) {
  const data = (error as { data?: { message?: string } })?.data;
  return (
    data?.message || (error as { error?: string })?.error || "Please try again."
  );
}

export function ManagePlanModal({
  open,
  onClose,
  billingInterval: billingIntervalProp = "monthly",
  canManageBilling = true,
  reason,
  focusPanel,
  onSuccess,
  initialIntervalSwitchStage = "idle",
}: ManagePlanModalProps) {
  const org = useSelector(selectOnlyOrganization);
  const cancelAt = org?.billing?.cancelAt
    ? new Date(org.billing.cancelAt as unknown as string | number)
    : null;
  const isPendingCancellation =
    cancelAt != null && cancelAt.getTime() > Date.now();
  const effectiveFocusPanel: ManagePlanFocusPanel | undefined =
    focusPanel ??
    (reason === "credit_limit_exceeded" || reason === "token_limit_exceeded"
      ? "credits"
      : reason === "no_seat_assigned"
        ? "seats"
        : reason === "deploy_quota_exceeded"
          ? "deployed_apps"
          : undefined);
  const reasonBanner = reason ? REASON_BANNER_CONFIG[reason] : undefined;
  const resetDate = formatResetDate(org?.billing?.expiryDate);
  const currentUser = useSelector(getCurrentUser);
  const [selectedCredits, setSelectedCredits] = useState<number | null>(null);
  type ModalView = "manage" | "confirm" | "cancel" | "topup";
  const [modalView, setModalView] = useState<ModalView>("manage");
  type ConfirmSnapshot = {
    builderSeatDelta: number;
    builderSeatDeltaPrice: number;
    deployedAppDelta: number;
    deployedAppDeltaPrice: number;
    creditsDeltaPrice: number;
    creditsChanged: boolean;
    currentPlanCredits: number;
    selectedPackCredits: number;
    totalAdditionalPrice: number;
    intervalSuffix: string;
    hasImmediateCharges: boolean;
    hasDowngradeChanges: boolean;
    isDowngradeBelowUsage: boolean;
    currentUsageCredits: number;
    proratedAmount: number | null;
    proratedLoading: boolean;
  };
  const [confirmSnapshot, setConfirmSnapshot] =
    useState<ConfirmSnapshot | null>(null);
  const {
    data: builderSeatsData,
    isLoading: isBuilderSeatsLoading,
    isError: hasBuilderSeatsError,
  } = useListBuilderSeatsQuery(undefined, {
    skip: !open,
    refetchOnMountOrArgChange: true,
    refetchOnFocus: true,
    refetchOnReconnect: true,
  });
  const currentBuilderSeats = builderSeatsData?.totalPurchasedSeats ?? 0;
  const currentBuilderSeatUsage = builderSeatsData?.builders.length ?? 0;
  const currentUserHasBuilderSeat = useMemo(() => {
    if (!currentUser || !builderSeatsData?.builders) {
      return null;
    }

    const normalizedEmail = currentUser.email.trim().toLowerCase();
    return builderSeatsData.builders.some(
      (builder) =>
        builder.userId === currentUser.id ||
        builder.email.trim().toLowerCase() === normalizedEmail,
    );
  }, [builderSeatsData?.builders, currentUser]);

  const showAssignModal =
    open && reason != null && currentUserHasBuilderSeat === false;
  const builderSeatCountUnavailable =
    isBuilderSeatsLoading || hasBuilderSeatsError || builderSeatsData == null;
  const [builderSeats, setBuilderSeats] = useState(currentBuilderSeats);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState<{
    top: number;
    left: number;
    width: number;
  } | null>(null);
  const [updateSubscription, { isLoading }] = useUpdateSubscriptionMutation();
  const [createPortalSession] = useCreatePortalSessionMutation();
  const [changeInterval, { isLoading: isChangingInterval }] =
    useChangeIntervalMutation();
  const intervalSwitchFlagOn =
    useFeatureFlag(Flag.ENABLE_BILLING_INTERVAL_SWITCH) === true;
  // 'idle' → CTA visible. 'confirm' → inline panel below Plan Summary Bar
  // walks the user through the schedule action. 'cancel-confirm' → tiny
  // confirm before releasing an already-scheduled switch.
  const [intervalSwitchStage, setIntervalSwitchStage] =
    useState<IntervalSwitchStage>(initialIntervalSwitchStage);
  // Apply initialIntervalSwitchStage every time the modal is opened so the
  // banner's "Switch to annual" / "Cancel switch" entry points land on
  // their target view, not the previous session's stage. afterClose handles
  // resetting back to "idle" when the modal closes.
  useEffect(() => {
    if (open) {
      setIntervalSwitchStage(initialIntervalSwitchStage);
    }
  }, [open, initialIntervalSwitchStage]);
  // Read-only observer of the manual top up mutation in AutoTopUpPanel — same
  // fixedCacheKey lets us see its isLoading state. We don't trigger from here.
  const [, { isLoading: isManualTopupCharging }] = useManualTopupChargeMutation(
    { fixedCacheKey: MANUAL_TOPUP_CACHE_KEY },
  );
  const [previewInvoice] = usePreviewInvoiceMutation();
  const {
    data: planSummary,
    isLoading: isPlanSummaryLoading,
    isError: hasPlanSummaryError,
  } = useGetPlanSummaryQuery(undefined, {
    skip: !open || !canManageBilling,
    refetchOnMountOrArgChange: true,
    refetchOnFocus: true,
    refetchOnReconnect: true,
  });
  // Powers the "Buy credits" button next to "Current plan" in the credit
  // panel. `available` mirrors /spending's eligibility gate (plan
  // ineligible / canceled / trialing). When unavailable, the button is
  // omitted so we don't open a Top up view the org can't actually use.
  const { data: autoTopupConfig } = useGetAutoTopupConfigQuery(undefined, {
    skip: !open || !canManageBilling,
  });
  // Manual refill (Buy/Refill credits → /v1/billing/auto-topup/manual-charge)
  // is its own LD-flagged feature, independent of auto-topup. Hide the
  // trigger button + the entire TopUpView mode when off so the admin
  // doesn't open a flow the server-side gate will reject.
  const refillFeatureEnabled =
    useFeatureFlag(Flag.BILLING_REFILL_ENABLED) === true;
  const triggerRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const currentPlanCredits = planSummary?.currentPlanCredits ?? 0;
  const currentUsageCredits = planSummary?.currentUsageCredits ?? 0;
  const topupCreditsPurchased = planSummary?.topupCreditsPurchased ?? 0;
  const topupCreditsUsed = planSummary?.topupCreditsUsed ?? 0;
  // Server folds active topup rows into currentPlanCredits for the Usage tile and
  // segmented bar (planSummary.ts:189). For the Manage-Plan dropdown + "Current
  // plan" label we need the *base* recurring pack size, not the fold — otherwise
  // an org that bought a 100/mo pack + a 150 topup sees `250 Org Credits` and the
  // 250 pack option marked "Current". Subtract topupCreditsPurchased to recover
  // the base; floor at 0 defensively.
  const basePlanCredits = Math.max(
    0,
    currentPlanCredits - topupCreditsPurchased,
  );
  const currentDeployedApps = planSummary?.deployedAppsAdditional ?? 0;
  const currentDeployedAppsUsage = planSummary?.deployedAppsUsed ?? 0;
  const deployedAppsIncluded = planSummary?.deployedAppsIncluded ?? 0;
  const deployedAppCountUnavailable =
    isPlanSummaryLoading || hasPlanSummaryError || planSummary == null;
  const effectiveBillingInterval =
    planSummary?.billingInterval ?? billingIntervalProp;
  const billingInterval = useMemo(() => {
    const billing = org?.billing as
      | { billingInterval?: string; interval?: string }
      | undefined;
    const rawInterval =
      billing?.billingInterval ?? billing?.interval ?? effectiveBillingInterval;
    return rawInterval === "annual" ? "annual" : "monthly";
  }, [effectiveBillingInterval, org?.billing]);
  const availableCreditPackOptions = useMemo(
    () =>
      AI_CREDIT_PACK_OPTIONS.filter(
        (option) => option.billingInterval === billingInterval,
      ),
    [billingInterval],
  );
  const selectedPack = useMemo(
    () =>
      availableCreditPackOptions.find(
        (option) => option.credits === selectedCredits,
      ) ?? null,
    [availableCreditPackOptions, selectedCredits],
  );
  const currentPack = useMemo(
    () =>
      availableCreditPackOptions.find(
        (option) => option.credits === basePlanCredits,
      ) ?? null,
    [availableCreditPackOptions, basePlanCredits],
  );
  const selectedPrice = selectedPack ? selectedPack.priceUsd : 0;
  const currentPackPrice = currentPack?.priceUsd ?? 0;
  // selectedCredits === 0 is a deliberate "remove all credit packs" choice and
  // produces a negative delta equal to the current pack price. It is a real
  // change, not the "no selection" sentinel (null).
  // Compare against basePlanCredits (the recurring pack only, with topup
  // folded out). Topup credits don't live on the recurring subscription line
  // item, so they're irrelevant to whether selecting "No additional credits"
  // is a real change for the recurring pack the user is editing here.
  const isZeroCreditsSelection = selectedCredits === 0 && basePlanCredits > 0;
  const creditsDeltaPrice = selectedPack
    ? selectedPrice - currentPackPrice
    : isZeroCreditsSelection
      ? -currentPackPrice
      : 0;
  const pricePerSeatAnnual = planSummary?.pricePerSeatAnnual ?? 1200;
  const pricePerSeatMonthly = planSummary?.pricePerSeatMonthly ?? 125;
  // CRITICAL: these useMemo hooks live up here, before the
  // `!canManageBilling` / loading / showAssignModal / past_due early returns
  // farther down (~line 630, 654, 680, 750). Putting them after any of those
  // returns gives this component instance a different hooks-call count on
  // renders that hit the early-return path vs. ones that don't, which
  // silently corrupts React's hook state and crashes the Usage page after
  // a Stripe redirect remount (boundary catches it → "Oops"). The other
  // useMemo in this file all sit alongside this block for the same reason.
  //
  // currentPackAnnualPrice: annual-interval equivalent of the active credit
  // pack, for the price comparison in the schedule-switch confirm panel.
  const currentPackAnnualPrice = useMemo(() => {
    if (basePlanCredits <= 0) return 0;
    const annualMatch = AI_CREDIT_PACK_OPTIONS.find(
      (o) => o.billingInterval === "annual" && o.credits === basePlanCredits,
    );
    return annualMatch?.priceUsd ?? 0;
  }, [basePlanCredits]);
  // annualSavingsPct: naive seat-line savings ("Save N%"). Returns null
  // when either price is missing or the math is non-positive, which the
  // CTA reads as "hide the pill."
  const annualSavingsPct = useMemo(() => {
    if (!pricePerSeatMonthly || !pricePerSeatAnnual) return null;
    const yearlyAtMonthly = pricePerSeatMonthly * 12;
    if (yearlyAtMonthly <= 0) return null;
    const pct =
      ((yearlyAtMonthly - pricePerSeatAnnual) / yearlyAtMonthly) * 100;
    return pct > 0 ? Math.round(pct) : null;
  }, [pricePerSeatAnnual, pricePerSeatMonthly]);
  const builderSeatPrice =
    billingInterval === "annual" ? pricePerSeatAnnual : pricePerSeatMonthly;
  const intervalSuffix = billingInterval === "annual" ? "/yr" : "/mo";
  const builderSeatDelta = builderSeats - currentBuilderSeats;
  const builderSeatDeltaPrice = builderSeatDelta * builderSeatPrice;
  // The server returns null for these fields only when Stripe itself is
  // unreachable (checkout would fail in that case anyway). We fall back to
  // the constants below rather than disabling the stepper.
  const deployedAppPriceMonthly =
    planSummary?.deployedAppPriceMonthly ??
    FALLBACK_DEPLOYED_APP_PRICE_MONTHLY_USD;
  const deployedAppPriceAnnual =
    planSummary?.deployedAppPriceAnnual ??
    FALLBACK_DEPLOYED_APP_PRICE_ANNUAL_USD;
  const deployedAppPrice =
    billingInterval === "annual"
      ? deployedAppPriceAnnual
      : deployedAppPriceMonthly;
  const [deployedApps, setDeployedApps] = useState(currentDeployedApps);
  const deployedAppDelta = deployedApps - currentDeployedApps;
  const deployedAppDeltaPrice = deployedAppDelta * deployedAppPrice;
  const totalAdditionalPrice =
    builderSeatDeltaPrice + deployedAppDeltaPrice + creditsDeltaPrice;
  const creditsChanged =
    selectedCredits !== null &&
    selectedCredits !== basePlanCredits &&
    (selectedPack !== null || isZeroCreditsSelection);
  const hasPlanChanges =
    builderSeats !== currentBuilderSeats ||
    deployedApps !== currentDeployedApps ||
    creditsChanged;
  const isCreditDowngrade =
    creditsChanged &&
    selectedCredits != null &&
    selectedCredits < basePlanCredits;
  // When downgrading, the new cap is selectedPack.credits, or 0 if the user
  // chose "No additional credits". Any positive usage exceeds a 0 cap, so
  // dropping to zero with non-zero usage always triggers the warning.
  const newCreditCap = selectedPack ? selectedPack.credits : 0;
  const isDowngradeBelowUsage =
    creditsChanged &&
    isCreditDowngrade &&
    newCreditCap < basePlanCredits &&
    currentUsageCredits > newCreditCap;
  const hasImmediateCharges =
    builderSeatDelta > 0 ||
    deployedAppDelta > 0 ||
    (creditsChanged && !isCreditDowngrade);
  const hasDowngradeChanges =
    builderSeatDelta < 0 || deployedAppDelta < 0 || isCreditDowngrade;

  const [cachedProratedAmount, setCachedProratedAmount] = useState<
    number | null
  >(null);
  const [proratedLoading, setProratedLoading] = useState(false);
  const previewTimerRef = useRef<ReturnType<typeof setTimeout>>();

  const fetchPreview = useCallback(() => {
    if (!hasImmediateCharges || !open) {
      setCachedProratedAmount(null);
      setProratedLoading(false);
      return;
    }
    const changes: Array<{
      unitType: "builder_seat" | "deployed_app" | "ai_credits";
      quantity: number;
      aiCreditPackSize?: number;
    }> = [];
    if (builderSeatDelta > 0) {
      changes.push({ unitType: "builder_seat", quantity: builderSeats });
    }
    if (deployedAppDelta > 0) {
      changes.push({ unitType: "deployed_app", quantity: deployedApps });
    }
    if (creditsChanged && !isCreditDowngrade && selectedPack) {
      changes.push({
        unitType: "ai_credits",
        quantity: 1,
        aiCreditPackSize: selectedPack.credits,
      });
    }
    if (changes.length === 0) return;
    setProratedLoading(true);
    previewInvoice({ changes, billingInterval })
      .unwrap()
      .then((result) => {
        setCachedProratedAmount(result.proratedAmountDue);
        setProratedLoading(false);
      })
      .catch(() => {
        setCachedProratedAmount(null);
        setProratedLoading(false);
      });
  }, [
    hasImmediateCharges,
    open,
    builderSeats,
    builderSeatDelta,
    deployedApps,
    deployedAppDelta,
    creditsChanged,
    isCreditDowngrade,
    selectedPack,
    billingInterval,
    previewInvoice,
  ]);

  useEffect(() => {
    clearTimeout(previewTimerRef.current);
    // Don't re-fetch the preview while a subscription update is in flight;
    // the partial mutations would produce misleading proration amounts.
    if (isLoading) return;
    if (!hasImmediateCharges) {
      setCachedProratedAmount(null);
      setProratedLoading(false);
      return;
    }
    setProratedLoading(true);
    previewTimerRef.current = setTimeout(fetchPreview, 400);
    return () => clearTimeout(previewTimerRef.current);
  }, [hasImmediateCharges, fetchPreview, isLoading]);

  useEffect(() => {
    // Don't update the confirmation snapshot while the subscription
    // update is in flight — the displayed totals should stay frozen.
    if (isLoading) return;
    if (modalView === "confirm" && confirmSnapshot) {
      setConfirmSnapshot((prev) =>
        prev
          ? { ...prev, proratedAmount: cachedProratedAmount, proratedLoading }
          : prev,
      );
    }
  }, [cachedProratedAmount, proratedLoading, isLoading]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!open) {
      return;
    }

    setBuilderSeats(currentBuilderSeats);
  }, [currentBuilderSeats, open]);

  useEffect(() => {
    if (!open) {
      return;
    }

    setDeployedApps(currentDeployedApps);
  }, [currentDeployedApps, open]);

  useEffect(() => {
    if (!open) return;
    if (
      basePlanCredits > 0 &&
      availableCreditPackOptions.some(
        (option) => option.credits === basePlanCredits,
      )
    ) {
      setSelectedCredits(basePlanCredits);
    } else if (basePlanCredits === 0) {
      // The org has no recurring pack today; default to the
      // "No additional credits" sentinel so it appears as the active
      // dropdown value. This is also the only initial state that
      // doesn't immediately surface a "creditsChanged" intent.
      setSelectedCredits(0);
    } else {
      // basePlanCredits > 0 but the value isn't in the predefined option
      // set (e.g., a stacked-pack legacy quantity like 100 + 500 = 600).
      // Use the `null` sentinel ("no selection made yet") so the modal
      // doesn't auto-arm a downgrade-to-zero on open — clicking Checkout
      // without interacting with the dropdown would otherwise silently
      // remove all credit packs because isZeroCreditsSelection would be
      // true with selectedCredits=0 and basePlanCredits>0.
      setSelectedCredits(null);
    }
  }, [open, basePlanCredits, availableCreditPackOptions]);

  useEffect(() => {
    if (
      selectedCredits != null &&
      selectedCredits !== 0 &&
      !availableCreditPackOptions.some(
        (option) => option.credits === selectedCredits,
      )
    ) {
      setSelectedCredits(null);
    }
  }, [availableCreditPackOptions, selectedCredits]);

  useEffect(() => {
    if (!isDropdownOpen) {
      setDropdownPosition(null);
      return;
    }

    const updateDropdownPosition = () => {
      const rect = triggerRef.current?.getBoundingClientRect();
      if (!rect) {
        return;
      }

      setDropdownPosition({
        top: rect.bottom + 8,
        left: rect.left,
        width: rect.width,
      });
    };

    updateDropdownPosition();

    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        !dropdownRef.current?.contains(target) &&
        !triggerRef.current?.contains(target)
      ) {
        setIsDropdownOpen(false);
      }
    };

    window.addEventListener("resize", updateDropdownPosition);
    window.addEventListener("scroll", updateDropdownPosition, true);
    document.addEventListener("mousedown", handlePointerDown);
    return () => {
      window.removeEventListener("resize", updateDropdownPosition);
      window.removeEventListener("scroll", updateDropdownPosition, true);
      document.removeEventListener("mousedown", handlePointerDown);
    };
  }, [isDropdownOpen]);

  const handleSelectCredits = (credits: number | null) => {
    setSelectedCredits(credits);
    setIsDropdownOpen(false);
  };

  const performCheckout = async () => {
    let seatsUpdated = false;
    let deployedAppsUpdated = false;
    let creditsUpdated = false;

    try {
      const responses = [];

      if (builderSeats !== currentBuilderSeats) {
        const seatResponse = await updateSubscription({
          unitType: "builder_seat",
          // The API expects the new total seat quantity, not the delta.
          quantity: builderSeats,
          billingInterval,
        }).unwrap();
        seatsUpdated = true;
        responses.push(seatResponse);
      }

      if (deployedApps !== currentDeployedApps) {
        const deployResponse = await updateSubscription({
          unitType: "deployed_app",
          quantity: deployedApps,
          billingInterval,
        }).unwrap();
        deployedAppsUpdated = true;
        responses.push(deployResponse);
      }

      if (creditsChanged) {
        // Going to zero ("No additional credits") sends quantity: 0 and no
        // aiCreditPackSize; the backend removes all existing credit-pack line
        // items. Any other change sends quantity: 1 plus the chosen pack size.
        const creditsResponse = await updateSubscription({
          unitType: "ai_credits",
          quantity: selectedPack ? 1 : 0,
          billingInterval,
          aiCreditPackSize: selectedPack?.credits,
        }).unwrap();
        creditsUpdated = true;
        responses.push(creditsResponse);
      }

      if (responses.length === 0) {
        return;
      }

      sendSuccessUINotification({
        message: responses.at(-1)?.message ?? "Plan updated successfully",
        placement: NotificationPosition.top,
      });
      if (onSuccess) {
        onSuccess();
      } else {
        onClose();
      }
    } catch (error: unknown) {
      const detail = getSubscriptionUpdateErrorDetail(error);
      const parts: string[] = [];
      if (seatsUpdated && builderSeats !== currentBuilderSeats) {
        parts.push("AI Builder seats were updated");
      }
      if (deployedAppsUpdated && deployedApps !== currentDeployedApps) {
        parts.push("Deployed apps were updated");
      }
      const failedPart =
        !seatsUpdated && builderSeats !== currentBuilderSeats
          ? "AI Builder seats"
          : !deployedAppsUpdated && deployedApps !== currentDeployedApps
            ? "Deployed apps"
            : !creditsUpdated && creditsChanged
              ? "AI credits"
              : "your plan";
      const message =
        parts.length > 0
          ? `${parts.join(" and ")}, but failed to update ${failedPart}. ${detail}`
          : `Failed to update ${failedPart}. ${detail}`;

      sendErrorUINotification({
        message,
        placement: NotificationPosition.top,
      });
    }
  };

  if (!canManageBilling) {
    return (
      <ContactAdminBillingModal
        open={open}
        onClose={onClose}
        currentUserHasBuilderSeat={currentUserHasBuilderSeat}
        reason={reason}
      />
    );
  }

  // Compute past_due gate up here so the earlier seat-assignment / loading
  // guards know to skip themselves when the modal is open *because* of a
  // past_due AI block. Without this, a seatless admin on a past_due org
  // would be routed to AssignBuilderSeatsModal — telling them to assign
  // themselves a seat — when the actual blocker is the failed renewal
  // payment, which has nothing to do with seat assignment.
  //
  // We trust the server's `reason` here. checkAiQuota only emits
  // 'payment_past_due' for TEAMS orgs whose stripe_sub_status is 'past_due',
  // so the reason alone is a reliable signal. (An earlier draft AND'd in a
  // client-side stripeSubStatus check; that was dead code via boolean
  // absorption — A && (B || A) ≡ A.)
  const isPastDueAiBlock = reason === "payment_past_due";

  if (
    !isPastDueAiBlock &&
    open &&
    reason != null &&
    currentUserHasBuilderSeat === null
  ) {
    return (
      <Modal
        open={open}
        onCancel={onClose}
        footer={null}
        width={1120}
        destroyOnClose
        className="tailwind"
        modalRender={(modal) => (
          <div className="overflow-hidden rounded-lg">{modal}</div>
        )}
        bodyStyle={{ padding: 0 }}
      >
        <div className="flex h-[200px] items-center justify-center bg-white">
          <span className="text-sm text-gray-400">Loading…</span>
        </div>
      </Modal>
    );
  }

  if (!isPastDueAiBlock && showAssignModal) {
    return (
      <AssignBuilderSeatsModal
        open={open}
        onClose={onClose}
        onSuccess={onSuccess}
        showNoSeatBanner
      />
    );
  }

  const handleOpenPortalSession = async (
    flow?: "payment_method_update" | "subscription_cancel",
    event?: React.MouseEvent,
  ) => {
    try {
      const result = await createPortalSession({
        flow,
        returnTo: "/usage",
      }).unwrap();
      if (event?.metaKey || event?.ctrlKey) {
        window.open(result.portalUrl, "_blank");
      } else {
        window.location.assign(result.portalUrl);
      }
    } catch {
      sendErrorUINotification({ message: "Failed to open billing portal." });
    }
  };

  // When the subscription is past_due (failed renewal payment) we short-circuit
  // the regular plan-management UI. Editing seat counts, credit packs, or app
  // quantities first requires a working payment method — otherwise any
  // subscription update will fail at Stripe, leaving the org in the same state.
  //
  // We intentionally open the *default* Stripe billing portal (no flow_data)
  // rather than the focused payment_method_update flow: the default portal
  // surfaces the outstanding invoice with a "Pay" button right next to the
  // payment-method update, so the admin can update their card AND charge
  // the open invoice in a single session. The focused flow only updates the
  // PM and then waits for Stripe's scheduled retry (hours-to-days later),
  // which leaves the org blocked from our side until the next retry.
  const isPastDue =
    org?.billing?.stripeSubStatus === "past_due" ||
    reason === "payment_past_due";
  // Only short-circuit to the focused past_due recovery modal when the modal
  // was opened *because* of a past_due AI block (reason='payment_past_due').
  // When the user opens "Manage plan" themselves from the Usage page banner,
  // we still render the full plan-management UI so they can see what they
  // currently have — but the Checkout button is disabled (see below) because
  // a subscription update would fail at Stripe with the same declined card.
  if (isPastDue && reason === "payment_past_due") {
    return (
      <Modal
        open={open}
        onCancel={onClose}
        footer={null}
        width={480}
        destroyOnClose
        closable={false}
        className="tailwind"
        modalRender={(modal) => (
          <div className="overflow-hidden rounded-[8px]">{modal}</div>
        )}
        bodyStyle={{ padding: 0 }}
      >
        <div data-test="past-due-modal" className="flex flex-col bg-white">
          <div className="flex h-12 items-center justify-between border-b border-gray-200 pl-6 pr-[10px]">
            <div className="flex items-center gap-2">
              <span
                aria-hidden
                className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-red-50 text-[12px] font-semibold text-red-600"
              >
                !
              </span>
              <h2 className="!m-0 text-[16px] font-semibold leading-6 text-gray-950">
                Your last payment didn&apos;t go through
              </h2>
            </div>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="inline-flex h-8 w-8 cursor-pointer items-center justify-center rounded-md text-gray-500 transition hover:bg-gray-50 hover:text-gray-700"
            >
              <span className="text-[24px] font-light leading-none">×</span>
            </button>
          </div>

          <div className="px-6 py-5">
            <p
              className="m-0 text-[14px] leading-6"
              style={{ color: "#44505f" }}
            >
              Your subscription is paused because the most recent invoice
              couldn&apos;t be charged. Open the billing portal to update your
              payment method and pay the outstanding invoice — access to Clark
              AI and other paid features will be restored as soon as the payment
              goes through.
            </p>
            {resetDate && (
              <p
                data-test="past-due-access-until"
                className="m-0 mt-2 text-[12px] leading-4 text-gray-500"
              >
                Access ends on {resetDate} unless the outstanding invoice is
                paid.
              </p>
            )}
          </div>

          <div className="flex items-center justify-end gap-3 border-t border-gray-200 px-6 py-4">
            <button
              type="button"
              onClick={onClose}
              className="inline-flex h-10 cursor-pointer items-center justify-center rounded-md border border-gray-200 bg-white px-4 text-sm font-medium text-gray-900 transition hover:bg-gray-50"
            >
              Not now
            </button>
            <button
              type="button"
              data-test="past-due-update-payment-btn"
              onClick={(event) => handleOpenPortalSession(undefined, event)}
              className="inline-flex h-10 cursor-pointer items-center justify-center rounded-md px-5 text-sm font-medium !text-white transition hover:bg-black/90"
              style={{ background: "#000" }}
            >
              Open billing portal
            </button>
          </div>
        </div>
      </Modal>
    );
  }

  const totalCurrentPrice =
    currentBuilderSeats * builderSeatPrice +
    currentDeployedApps * deployedAppPrice +
    currentPackPrice;

  // currentPackAnnualPrice + annualSavingsPct (useMemo) moved earlier in the
  // function body, alongside the other useMemo hooks — see comment there.
  const totalAnnualPrice =
    currentBuilderSeats * pricePerSeatAnnual +
    currentDeployedApps * deployedAppPriceAnnual +
    currentPackAnnualPrice;

  const pendingInterval = planSummary?.pendingInterval ?? null;
  const pendingEffectiveAt = planSummary?.pendingIntervalEffectiveAt ?? null;
  const hasPendingSwitch =
    pendingInterval != null && pendingEffectiveAt != null;
  const pendingEffectiveLabel = pendingEffectiveAt
    ? formatResetDate(pendingEffectiveAt)
    : null;
  // Surface the CTA only for orgs in a state where the server-side handler
  // will actually accept the switch. Mirrors the gates in ChangeIntervalHandler
  // (active subscription, no cancellation pending, monthly today) so we don't
  // show a button that 4xxs on click.
  const canSwitchToAnnual =
    intervalSwitchFlagOn &&
    canManageBilling &&
    !isPendingCancellation &&
    !effectiveFocusPanel &&
    billingInterval === "monthly" &&
    pendingInterval !== "annual" &&
    planSummary != null &&
    planSummary.subscriptionStatus === "active";
  const showPendingPill = intervalSwitchFlagOn && hasPendingSwitch;

  // The default toast placement in this codebase is bottomRight (set in
  // dashboard/utils/notification.tsx), but for the interval-switch flow
  // we want the confirmation to land on top — the user just dismissed
  // a modal that occupied the upper half of the screen, so their eyes
  // are already there, and a bottom-right toast is easy to miss.
  const handleScheduleAnnualSwitch = async () => {
    try {
      await changeInterval({ billingInterval: "annual" }).unwrap();
      setIntervalSwitchStage("idle");
      sendSuccessUINotification({
        message: resetDate
          ? `Success! You'll be renewed onto an annual plan on ${resetDate}`
          : "Success! You'll be renewed onto an annual plan on your next renewal",
        placement: NotificationPosition.top,
      });
    } catch (err) {
      sendErrorUINotification({
        message: "Failed to schedule switch",
        description: getSubscriptionUpdateErrorDetail(err),
        placement: NotificationPosition.top,
      });
    }
  };

  const handleCancelPendingSwitch = async () => {
    try {
      // Server treats target === current as "release the schedule" and
      // clears pending_interval. The current interval is still 'monthly'
      // here — the conversion happens at period_end.
      await changeInterval({ billingInterval }).unwrap();
      setIntervalSwitchStage("idle");
      sendSuccessUINotification({
        message: "Scheduled switch canceled",
        placement: NotificationPosition.top,
      });
    } catch (err) {
      sendErrorUINotification({
        message: "Failed to cancel scheduled switch",
        description: getSubscriptionUpdateErrorDetail(err),
        placement: NotificationPosition.top,
      });
    }
  };

  // In the interval-switch stages we render a focused single-column
  // layout (header + receipt + footer), so 1000px is way too wide and
  // stretches the breakdown across empty space. Pin it to 520 for both
  // confirm and cancel-confirm.
  const modalWidth =
    modalView === "manage"
      ? intervalSwitchStage !== "idle"
        ? 520
        : effectiveFocusPanel
          ? 480
          : 1000
      : modalView === "confirm"
        ? 412
        : modalView === "topup"
          ? 640
          : 440;

  return (
    <>
      <Modal
        open={open}
        onCancel={() => {
          onClose();
        }}
        afterClose={() => {
          setModalView("manage");
          setConfirmSnapshot(null);
          setIntervalSwitchStage("idle");
        }}
        footer={null}
        width={modalWidth}
        destroyOnClose
        className="tailwind"
        closable={false}
        modalRender={(modal) => (
          <div className="overflow-hidden rounded-2xl">{modal}</div>
        )}
        bodyStyle={{ padding: 0 }}
      >
        {modalView === "manage" && (
          <div
            data-test="manage-plan-modal"
            className="flex max-h-[90vh] flex-col overflow-hidden bg-white"
          >
            {/* Header. During the interval-switch flow (confirm /
                cancel-confirm) we swap the title for stage-specific copy
                and a renewal-date subtitle, and hide the
                Update-credit-card / See-past-invoices shortcuts — they
                aren't relevant when the user is mid-decision on a
                deferred interval switch and would clutter the focused
                content below. */}
            <div className="flex shrink-0 items-center gap-1.5 border-b border-[#e8eaed] px-6 py-4">
              {intervalSwitchStage === "confirm" ? (
                <div className="flex flex-1 flex-col gap-0.5">
                  <h2 className="!m-0 text-[15px] font-semibold leading-5 tracking-[-0.3px] text-[#1f2633]">
                    Switch to annual billing
                  </h2>
                  <p className="m-0 text-[12px] leading-4 text-[#6c7689]">
                    Takes effect on your next renewal
                    {resetDate ? ` — ${resetDate}` : ""}
                  </p>
                </div>
              ) : intervalSwitchStage === "cancel-confirm" ? (
                <h2 className="!m-0 flex-1 text-[15px] font-semibold leading-5 tracking-[-0.3px] text-[#1f2633]">
                  Cancel scheduled switch?
                </h2>
              ) : (
                <h2 className="!m-0 flex-1 text-[15px] font-semibold leading-5 tracking-[-0.3px] text-[#1f2633]">
                  Manage your plan
                </h2>
              )}
              {intervalSwitchStage === "idle" &&
                !effectiveFocusPanel &&
                isPendingCancellation && (
                  <button
                    type="button"
                    onClick={(e) => {
                      handleOpenPortalSession(undefined, e);
                    }}
                    className="flex cursor-pointer items-center gap-1 rounded-[6px] border-0 bg-transparent py-1.5 pl-2 pr-3 text-xs font-medium leading-4 text-[#454d5f] transition hover:bg-[#f3f4f6]"
                  >
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M4 8h8M8.667 4.667 12 8l-3.333 3.333" />
                    </svg>
                    Reactivate plan
                  </button>
                )}
              {intervalSwitchStage === "idle" &&
              !effectiveFocusPanel &&
              !isPendingCancellation ? (
                <button
                  type="button"
                  onClick={(e) => {
                    handleOpenPortalSession("payment_method_update", e);
                  }}
                  className="flex cursor-pointer items-center gap-1 rounded-[6px] border-0 bg-transparent py-1.5 pl-2 pr-3 text-xs font-medium leading-4 text-[#454d5f] transition hover:bg-[#f3f4f6]"
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <rect x="2" y="4" width="12" height="8.667" rx="1.333" />
                    <path d="M2 7.333h12" />
                  </svg>
                  Update credit card
                </button>
              ) : null}
              {intervalSwitchStage === "idle" &&
              !effectiveFocusPanel &&
              !isPendingCancellation ? (
                <button
                  type="button"
                  onClick={(e) => {
                    handleOpenPortalSession(undefined, e);
                  }}
                  className="flex cursor-pointer items-center gap-1 rounded-[6px] border-0 bg-transparent py-1.5 pl-2 pr-3 text-xs font-medium leading-4 text-[#454d5f] transition hover:bg-[#f3f4f6]"
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M9.333 2H4.667A1.333 1.333 0 0 0 3.333 3.333v9.334A1.333 1.333 0 0 0 4.667 14h6.666a1.333 1.333 0 0 0 1.334-1.333V5.333L9.333 2Z" />
                    <path d="M9.333 2v3.333h3.334" />
                  </svg>
                  See past invoices
                </button>
              ) : null}
              <button
                type="button"
                onClick={onClose}
                className="flex cursor-pointer items-center justify-center rounded p-1 border-0 bg-transparent text-[#6c7689] transition hover:bg-[#f3f4f6]"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M12 4 4 12M4 4l8 8" />
                </svg>
              </button>
            </div>

            {/* Body — children opt out of flex-shrink so designed heights are preserved
                 and the body scrolls instead of compressing rows. flex-1 only
                 in the idle stage (where the scrollable cards live); in
                 confirm / cancel-confirm the content is short, so we let the
                 body hug it instead of stretching into an empty white band. */}
            <div
              className={`flex min-h-0 flex-col gap-6 overflow-y-auto p-6 [&>*]:shrink-0 ${
                intervalSwitchStage === "idle" ? "flex-1" : ""
              }`}
            >
              {isPastDue && (
                <div
                  data-test="manage-plan-past-due-banner"
                  className="flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2.5"
                >
                  <span
                    aria-hidden
                    className="mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-red-100 text-[12px] font-semibold text-red-600"
                  >
                    !
                  </span>
                  <div className="flex flex-1 flex-col gap-1">
                    <p className="m-0 text-[13px] font-medium leading-5 text-red-900">
                      Subscription past due — plan changes are paused
                    </p>
                    <p className="m-0 text-[12px] leading-4 text-red-900/80">
                      Your most recent invoice couldn&apos;t be charged. Open
                      the billing portal to update your card and pay the
                      outstanding invoice; plan changes will be re-enabled once
                      the payment goes through.
                    </p>
                  </div>
                  <button
                    type="button"
                    data-test="manage-plan-past-due-portal-btn"
                    onClick={(e) => handleOpenPortalSession(undefined, e)}
                    className="inline-flex h-8 shrink-0 cursor-pointer items-center justify-center rounded-md px-3 text-xs font-medium !text-white transition hover:bg-black/90"
                    style={{ background: "#000" }}
                  >
                    Open billing portal
                  </button>
                </div>
              )}
              {isPendingCancellation && (
                <div className="rounded border border-[rgba(255,159,53,0.24)] bg-[rgba(255,159,53,0.04)] p-2 text-xs leading-4 text-[#454d5f]">
                  Your plan is pending cancellation. Plan changes are disabled
                  until the cancellation is reversed.
                </div>
              )}
              {reasonBanner && !isPendingCancellation ? (
                <div className="rounded border border-[rgba(255,159,53,0.24)] bg-[rgba(255,159,53,0.04)] p-2 text-xs leading-4 text-[#454d5f]">
                  <p className="m-0 font-medium">{reasonBanner.headline}</p>
                  <p className="m-0 mt-0.5">
                    {reasonBanner.cta}
                    {resetDate && reason !== "deploy_quota_exceeded"
                      ? ` Credits reset on ${resetDate}.`
                      : null}
                  </p>
                </div>
              ) : null}

              {/* Plan Summary Bar — shown in idle + cancel-confirm; hidden
                  in confirm (the receipt breakdown takes over). The action
                  button on the right (Switch to annual / Cancel switch) is
                  idle-only: in cancel-confirm the footer owns Keep/Cancel,
                  so the bar just shows plan + price for context. */}
              {intervalSwitchStage === "confirm" ? null : (
                <div className="flex h-16 items-center gap-4 overflow-hidden rounded-lg bg-white p-4 shadow-[0_0_0_1px_rgba(31,38,51,0.12)]">
                  <div className="flex h-full w-8 shrink-0 items-center justify-center rounded bg-[#f3f4f6]">
                    <FolderUsersIcon className="h-4 w-4" />
                  </div>
                  <div className="flex flex-1 flex-col">
                    <div className="flex items-center gap-2">
                      <span className="text-[13px] font-medium leading-5 text-[#1f2633]">
                        Teams plan
                      </span>
                    </div>
                    <span className="text-xs leading-4 text-[#6c7689]">
                      {resetDate
                        ? `Renews ${resetDate}`
                        : `${billingInterval === "annual" ? "Annual" : "Monthly"} billing`}
                    </span>
                  </div>
                  <div className="flex shrink-0 items-center gap-3">
                    {intervalSwitchStage === "idle" && showPendingPill ? (
                      <button
                        type="button"
                        onClick={() => setIntervalSwitchStage("cancel-confirm")}
                        disabled={isChangingInterval}
                        className="inline-flex h-8 cursor-pointer items-center gap-1.5 rounded-[6px] border-0 bg-white px-3 text-xs font-medium leading-4 text-[#1f2633] shadow-[0_0_0_1px_rgba(31,38,51,0.16),0_1px_3px_rgba(31,38,51,0.08)] transition hover:bg-[#f9fafb] disabled:cursor-not-allowed disabled:text-[#a4aab7]"
                      >
                        Cancel switch
                      </button>
                    ) : intervalSwitchStage === "idle" && canSwitchToAnnual ? (
                      <button
                        type="button"
                        onClick={() => setIntervalSwitchStage("confirm")}
                        disabled={isChangingInterval}
                        className="inline-flex h-8 cursor-pointer items-center gap-1.5 rounded-[6px] border-0 bg-white px-3 text-xs font-medium leading-4 text-[#1f2633] shadow-[0_0_0_1px_rgba(31,38,51,0.16),0_1px_3px_rgba(31,38,51,0.08)] transition hover:bg-[#f9fafb] disabled:cursor-not-allowed disabled:text-[#a4aab7]"
                      >
                        Switch to annual
                        {annualSavingsPct != null ? (
                          <span className="inline-flex items-center rounded bg-[#e7f6ec] px-1.5 py-0.5 text-[10px] font-semibold text-[#18794e]">
                            Save {annualSavingsPct}%
                          </span>
                        ) : null}
                      </button>
                    ) : null}
                    <span className="text-[13px] font-medium leading-5 text-[#1f2633]">
                      {formatUsd(totalCurrentPrice)}
                      {intervalSuffix}
                    </span>
                  </div>
                </div>
              )}

              {/* cancel-confirm context line — sits below the Plan Summary
                  Bar (not in the modal title) so the user sees what they're
                  about to cancel. Footer holds Keep switch / Cancel switch. */}
              {intervalSwitchStage === "cancel-confirm" ? (
                <p className="m-0 text-[13px] leading-5 text-[#454d5f]">
                  You&apos;re scheduled to switch to annual billing
                  {pendingEffectiveLabel ? ` on ${pendingEffectiveLabel}` : ""}
                  {totalCurrentPrice * 12 - totalAnnualPrice > 0
                    ? ` and save ${formatUsd(totalCurrentPrice * 12 - totalAnnualPrice)}`
                    : ""}
                  .
                </p>
              ) : null}

              {/* Inline confirm panel — schedule switch.
                  Receipt-style breakdown: Item · Today · Annual, with a
                  rollup Total row and a "You save N%" row underneath.
                  The heading/subtitle live in the modal header now, so
                  the table sits flat in the body without a nested
                  card wrapper. */}
              {intervalSwitchStage === "confirm" &&
              canSwitchToAnnual &&
              !showPendingPill ? (
                <div className="space-y-3">
                  <div className="overflow-hidden rounded border border-[#e8eaed]">
                    {/* Column headers */}
                    <div className="grid grid-cols-[1fr_72px_82px] items-center gap-3 bg-[#f9fafb] px-3 py-1.5">
                      <span className="text-[10px] font-semibold uppercase leading-4 tracking-wide text-[#6c7689]">
                        Item
                      </span>
                      <span className="text-right text-[10px] font-semibold uppercase leading-4 tracking-wide text-[#6c7689]">
                        Today
                      </span>
                      <span className="text-right text-[10px] font-semibold uppercase leading-4 tracking-wide text-[#6c7689]">
                        Annual
                      </span>
                    </div>
                    {/* Builder licenses */}
                    <div className="grid grid-cols-[1fr_72px_82px] items-center gap-3 border-t border-[#e8eaed] bg-white px-3 py-2 text-[12px] leading-5">
                      <span className="text-[#454d5f]">
                        {currentBuilderSeats} Builder license
                        {currentBuilderSeats === 1 ? "" : "s"}
                      </span>
                      <span className="text-right tabular-nums text-[#6c7689]">
                        {formatUsd(currentBuilderSeats * pricePerSeatMonthly)}
                        /mo
                      </span>
                      <span className="text-right font-medium tabular-nums text-[#1f2633]">
                        {formatUsd(currentBuilderSeats * pricePerSeatAnnual)}
                        /yr
                      </span>
                    </div>
                    {/* Extra deployed apps — only show when non-zero so we
                        don't clutter the table with $0 rows for orgs that
                        are only paying for seats. */}
                    {currentDeployedApps > 0 ? (
                      <div className="grid grid-cols-[1fr_72px_82px] items-center gap-3 border-t border-[#e8eaed] bg-white px-3 py-2 text-[12px] leading-5">
                        <span className="text-[#454d5f]">
                          {currentDeployedApps} Extra deployed app
                          {currentDeployedApps === 1 ? "" : "s"}
                        </span>
                        <span className="text-right tabular-nums text-[#6c7689]">
                          {formatUsd(
                            currentDeployedApps * deployedAppPriceMonthly,
                          )}
                          /mo
                        </span>
                        <span className="text-right font-medium tabular-nums text-[#1f2633]">
                          {formatUsd(
                            currentDeployedApps * deployedAppPriceAnnual,
                          )}
                          /yr
                        </span>
                      </div>
                    ) : null}
                    {/* Credit pack — only show when there's actually a
                        recurring pack on the subscription. */}
                    {basePlanCredits > 0 ? (
                      <div className="grid grid-cols-[1fr_72px_82px] items-center gap-3 border-t border-[#e8eaed] bg-white px-3 py-2 text-[12px] leading-5">
                        <span className="text-[#454d5f]">
                          {basePlanCredits.toLocaleString()} AI credit pack
                        </span>
                        <span className="text-right tabular-nums text-[#6c7689]">
                          {formatUsd(currentPackPrice)}/mo
                        </span>
                        <span className="text-right font-medium tabular-nums text-[#1f2633]">
                          {formatUsd(currentPackAnnualPrice)}/yr
                        </span>
                      </div>
                    ) : null}
                    {/* Total */}
                    <div className="grid grid-cols-[1fr_72px_82px] items-center gap-3 border-t border-[#e8eaed] bg-[#f9fafb] px-3 py-2 text-[12px] leading-5">
                      <span className="font-semibold text-[#1f2633]">
                        Total
                      </span>
                      <span className="text-right tabular-nums text-[#6c7689]">
                        {formatUsd(totalCurrentPrice)}/mo
                      </span>
                      <span className="text-right font-semibold tabular-nums text-[#1f2633]">
                        {formatUsd(totalAnnualPrice)}/yr
                      </span>
                    </div>
                    {/* You save — derived from the totals so it stays
                        consistent with whatever rows above include. We
                        compare 12 months at the monthly rate vs. the
                        annual rate. Save % comes from annualSavingsPct
                        (seat-line) for the header chip — same source,
                        just inlined here instead of a separate pill. */}
                    {totalCurrentPrice * 12 - totalAnnualPrice > 0 ? (
                      <div className="grid grid-cols-[1fr_auto] items-center gap-3 border-t border-[#cde9d6] bg-[#eef9f1] px-3 py-2 text-[12px] leading-5">
                        <span className="font-medium text-[#18794e]">
                          You save
                          {annualSavingsPct != null
                            ? ` ${annualSavingsPct}%`
                            : ""}
                        </span>
                        <span className="text-right font-semibold tabular-nums text-[#18794e]">
                          {formatUsd(totalCurrentPrice * 12 - totalAnnualPrice)}
                          /yr
                        </span>
                      </div>
                    ) : null}
                  </div>
                  <ul className="m-0 list-none space-y-1 p-0 text-[12px] leading-5 text-[#454d5f]">
                    <li>
                      No charge today. You keep paying{" "}
                      {formatUsd(totalCurrentPrice)}/mo until
                      {resetDate ? ` ${resetDate}` : " your next renewal"}.
                    </li>
                    <li>You can cancel the switch anytime before then.</li>
                  </ul>
                </div>
              ) : null}

              {/* When the user is mid-switch (confirm or cancel-confirm),
                  collapse the rest of the modal to just the Plan Summary
                  Bar + the relevant inline panel above. Adjusting seats /
                  apps / credits / running checkout from this state would
                  conflate "schedule interval switch" with "edit plan,"
                  which the server-side handler rejects anyway. */}
              {intervalSwitchStage === "idle" ? (
                <>
                  {isPlanSummaryLoading ? (
                    <div className="rounded-lg border border-[#e8eaed] bg-[#f9fafb] px-4 py-3 text-xs text-[#6c7689]">
                      Loading current plan summary...
                    </div>
                  ) : null}
                  {hasPlanSummaryError ? (
                    <div className="rounded border border-[rgba(255,159,53,0.24)] bg-[rgba(255,159,53,0.04)] p-2 text-xs leading-4 text-[#454d5f]">
                      Unable to load plan summary. Showing fallback values.
                    </div>
                  ) : null}

                  {/* Cards */}
                  <div
                    className={
                      effectiveFocusPanel
                        ? "grid grid-cols-1 gap-6"
                        : "grid grid-cols-3 gap-6"
                    }
                  >
                    {(!effectiveFocusPanel ||
                      effectiveFocusPanel === "seats") && (
                      <div className="min-w-0">
                        <ManagePlanSeatPanel
                          billingInterval={billingInterval}
                          currentBuilderSeats={currentBuilderSeats}
                          currentBuilderSeatUsage={currentBuilderSeatUsage}
                          builderSeatCountUnavailable={
                            builderSeatCountUnavailable || isPendingCancellation
                          }
                          builderSeats={builderSeats}
                          onBuilderSeatsChange={setBuilderSeats}
                          pricePerSeatAnnual={pricePerSeatAnnual}
                          pricePerSeatMonthly={pricePerSeatMonthly}
                        />
                      </div>
                    )}
                    {(!effectiveFocusPanel ||
                      effectiveFocusPanel === "deployed_apps") && (
                      <div className="min-w-0">
                        <ManagePlanDeployPanel
                          billingInterval={billingInterval}
                          deployedAppsIncluded={deployedAppsIncluded}
                          currentDeployedApps={currentDeployedApps}
                          currentDeployedAppsUsage={currentDeployedAppsUsage}
                          deployedAppCountUnavailable={
                            deployedAppCountUnavailable || isPendingCancellation
                          }
                          deployedApps={deployedApps}
                          deployedAppPriceMonthly={deployedAppPriceMonthly}
                          deployedAppPriceAnnual={deployedAppPriceAnnual}
                          onDeployedAppsChange={setDeployedApps}
                        />
                      </div>
                    )}
                    {(!effectiveFocusPanel ||
                      effectiveFocusPanel === "credits") && (
                      <div className="min-w-0">
                        <ManagePlanCreditPanel
                          billingInterval={billingInterval}
                          availableCreditPackOptions={
                            availableCreditPackOptions
                          }
                          currentPlanCredits={basePlanCredits}
                          currentUsageCredits={currentUsageCredits}
                          topupCreditsPurchased={topupCreditsPurchased}
                          topupCreditsUsed={topupCreditsUsed}
                          selectedCredits={selectedCredits}
                          selectedPack={selectedPack}
                          isDropdownOpen={isDropdownOpen}
                          dropdownPosition={dropdownPosition}
                          triggerRef={triggerRef}
                          dropdownRef={dropdownRef}
                          disabled={isPendingCancellation}
                          onToggleDropdown={() =>
                            setIsDropdownOpen((previous) => !previous)
                          }
                          onSelectCredits={handleSelectCredits}
                          // Single-modal in-place transition into Top up mode —
                          // no nested antd Modal (would flicker the overlay).
                          // Hidden when (a) the manual-refill LD flag is off,
                          // OR (b) the org is ineligible per server eligibility
                          // (no payment method, plan ineligible, canceled,
                          // trialing). We don't open a flow the server will
                          // reject.
                          onBuyCredits={
                            refillFeatureEnabled && autoTopupConfig?.available
                              ? () => setModalView("topup")
                              : undefined
                          }
                        />
                      </div>
                    )}
                  </div>

                  {/* Top up credits panel retired from Manage Plan modal — the
                  full top up UI (manual + auto config + history) lives at
                  /spending. The Checkout button below still observes the
                  manual top up mutation via MANUAL_TOPUP_CACHE_KEY so it
                  disables while a charge initiated from /spending is
                  in flight. */}

                  {/* Summary */}
                  <div className="overflow-hidden rounded-lg shadow-[0_0_0_1px_rgba(31,38,51,0.12)]">
                    <div className="flex flex-col gap-2 border-b border-[#e8eaed] px-4 py-3 text-xs text-[#6c7689]">
                      {builderSeatDelta !== 0 ? (
                        <div className="flex items-center justify-between gap-4">
                          <span>
                            {builderSeatDelta > 0 ? "+" : ""}
                            {builderSeatDelta} AI Builder{" "}
                            {Math.abs(builderSeatDelta) === 1
                              ? "License"
                              : "Licenses"}
                          </span>
                          <span>
                            {builderSeatDeltaPrice > 0 ? "+" : "−"}
                            {formatUsd(Math.abs(builderSeatDeltaPrice))}
                            {intervalSuffix}
                          </span>
                        </div>
                      ) : null}
                      {deployedAppDelta !== 0 ? (
                        <div className="flex items-center justify-between gap-4">
                          <span>
                            {deployedAppDelta > 0 ? "+" : ""}
                            {deployedAppDelta} Deployed App
                            {Math.abs(deployedAppDelta) === 1 ? "" : "s"}
                          </span>
                          <span>
                            {deployedAppDeltaPrice > 0 ? "+" : "−"}
                            {formatUsd(Math.abs(deployedAppDeltaPrice))}
                            {intervalSuffix}
                          </span>
                        </div>
                      ) : null}
                      {creditsChanged ? (
                        <div className="flex items-center justify-between gap-4">
                          <span>
                            AI Credits:{" "}
                            {basePlanCredits > 0
                              ? `${basePlanCredits.toLocaleString()} → `
                              : ""}
                            {(selectedPack?.credits ?? 0).toLocaleString()}
                          </span>
                          <span>
                            {creditsDeltaPrice >= 0 ? "+" : "−"}
                            {formatUsd(Math.abs(creditsDeltaPrice))}
                            {intervalSuffix}
                          </span>
                        </div>
                      ) : null}
                      {!hasPlanChanges ? (
                        <div className="text-xs text-[#a4aab7]">
                          {effectiveFocusPanel === "seats"
                            ? "Adjust seats to update your plan."
                            : effectiveFocusPanel === "deployed_apps"
                              ? "Adjust deployed apps to update your plan."
                              : effectiveFocusPanel === "credits"
                                ? "Choose a credit pack to update your plan."
                                : "Adjust seats, deployed apps, and/or choose a credit pack to update your plan."}
                        </div>
                      ) : null}
                    </div>
                    <div className="flex flex-col gap-2 px-4 py-3">
                      <div className="flex items-center justify-between gap-4">
                        <span className="text-xs font-medium text-[#1f2633]">
                          Change in cost
                        </span>
                        <span className="text-[13px] font-semibold text-[#1f2633]">
                          {totalAdditionalPrice > 0
                            ? "+"
                            : totalAdditionalPrice < 0
                              ? "−"
                              : ""}
                          {formatUsd(Math.abs(totalAdditionalPrice))}
                          {intervalSuffix}
                        </span>
                      </div>
                      {hasImmediateCharges ? (
                        <p className="m-0 text-[11px] leading-4 text-[#a4aab7]">
                          {proratedLoading
                            ? "Calculating prorated charge…"
                            : cachedProratedAmount != null
                              ? `An additional prorated charge of ${formatUsd(cachedProratedAmount, 2)} will be applied immediately for the remainder of your current billing period.`
                              : "An additional prorated charge will be applied immediately for the remainder of your current billing period."}
                        </p>
                      ) : null}
                      {hasDowngradeChanges ? (
                        <p className="m-0 text-[11px] leading-4 text-[#a4aab7]">
                          This change will take effect at the start of your next
                          billing period.
                        </p>
                      ) : null}
                      {isDowngradeBelowUsage ? (
                        <p className="m-0 text-[11px] leading-4 text-amber-600">
                          You&apos;ve used{" "}
                          {Math.ceil(currentUsageCredits).toLocaleString()}{" "}
                          credits this period, which exceeds the{" "}
                          {(selectedPack?.credits ?? 0).toLocaleString()} credit
                          pack. No additional credits will be available until
                          the next billing cycle.
                        </p>
                      ) : null}
                    </div>
                  </div>
                </>
              ) : null}
            </div>

            {/* Footer — three modes:
                  idle           → Cancel plan (left)  + Checkout (right)
                  confirm        → Not now + Schedule switch (both right)
                  cancel-confirm → Keep switch + Cancel switch (both right)
                For confirm / cancel-confirm we group both actions on
                the right (justify-end) so the secondary "back out"
                button sits next to the primary, matching a standard
                form-confirm layout. */}
            <div
              className={`flex shrink-0 items-center gap-2 px-6 py-4 shadow-[0_-1px_3px_rgba(31,38,51,0.06),0_-1px_0_rgba(31,38,51,0.12)] ${
                intervalSwitchStage === "idle"
                  ? "justify-between"
                  : "justify-end"
              }`}
            >
              {intervalSwitchStage === "idle" &&
              !effectiveFocusPanel &&
              !isPendingCancellation ? (
                <button
                  type="button"
                  onClick={() => setModalView("cancel")}
                  className="inline-flex h-8 min-w-[56px] cursor-pointer items-center justify-center rounded-[6px] border-0 bg-white px-3 text-xs font-medium leading-4 text-[#1f2633] shadow-[0_0_0_1px_rgba(31,38,51,0.12),0_1px_3px_rgba(31,38,51,0.12)] transition hover:bg-[#f9fafb]"
                >
                  Cancel plan
                </button>
              ) : intervalSwitchStage === "idle" ? (
                <div />
              ) : null}
              {intervalSwitchStage === "confirm" ? (
                <button
                  type="button"
                  onClick={() => setIntervalSwitchStage("idle")}
                  disabled={isChangingInterval}
                  className="inline-flex h-8 min-w-[56px] cursor-pointer items-center justify-center rounded-[6px] border-0 bg-white px-3 text-xs font-medium leading-4 text-[#1f2633] shadow-[0_0_0_1px_rgba(31,38,51,0.12),0_1px_3px_rgba(31,38,51,0.12)] transition hover:bg-[#f9fafb] disabled:cursor-not-allowed disabled:text-[#a4aab7]"
                >
                  Not now
                </button>
              ) : intervalSwitchStage === "cancel-confirm" ? (
                <button
                  type="button"
                  onClick={() => setIntervalSwitchStage("idle")}
                  disabled={isChangingInterval}
                  className="inline-flex h-8 min-w-[56px] cursor-pointer items-center justify-center rounded-[6px] border-0 bg-white px-3 text-xs font-medium leading-4 text-[#1f2633] shadow-[0_0_0_1px_rgba(31,38,51,0.12),0_1px_3px_rgba(31,38,51,0.12)] transition hover:bg-[#f9fafb] disabled:cursor-not-allowed disabled:text-[#a4aab7]"
                >
                  Keep switch
                </button>
              ) : null}
              {intervalSwitchStage === "confirm" &&
              canSwitchToAnnual &&
              !showPendingPill ? (
                <button
                  type="button"
                  onClick={handleScheduleAnnualSwitch}
                  disabled={isChangingInterval}
                  className="inline-flex cursor-pointer items-center justify-center rounded-lg !border-0 !bg-[#1f2633] px-4 py-2 text-xs font-medium leading-4 !text-white !shadow-none transition hover:!bg-black disabled:cursor-not-allowed disabled:!bg-[#a4aab7]"
                >
                  {isChangingInterval ? "Scheduling…" : "Schedule switch"}
                </button>
              ) : intervalSwitchStage === "cancel-confirm" ? (
                <button
                  type="button"
                  onClick={handleCancelPendingSwitch}
                  disabled={isChangingInterval}
                  className="inline-flex cursor-pointer items-center justify-center rounded-lg !border-0 !bg-[#1f2633] px-4 py-2 text-xs font-medium leading-4 !text-white !shadow-none transition hover:!bg-black disabled:cursor-not-allowed disabled:!bg-[#a4aab7]"
                >
                  {isChangingInterval ? "Canceling…" : "Cancel switch"}
                </button>
              ) : (
                <Tooltip
                  title={
                    isPastDue
                      ? "Resolve the outstanding invoice before changing your plan. Stripe will reject any subscription update while your subscription is past due."
                      : ""
                  }
                  placement="topRight"
                >
                  <button
                    type="button"
                    data-test="manage-plan-checkout-btn"
                    onClick={() => {
                      setConfirmSnapshot({
                        builderSeatDelta,
                        builderSeatDeltaPrice,
                        deployedAppDelta,
                        deployedAppDeltaPrice,
                        creditsDeltaPrice,
                        creditsChanged,
                        currentPlanCredits: basePlanCredits,
                        selectedPackCredits: selectedPack?.credits ?? 0,
                        totalAdditionalPrice,
                        intervalSuffix,
                        hasImmediateCharges,
                        hasDowngradeChanges,
                        isDowngradeBelowUsage,
                        currentUsageCredits,
                        proratedAmount: cachedProratedAmount,
                        proratedLoading,
                      });
                      setModalView("confirm");
                    }}
                    disabled={
                      isLoading ||
                      isManualTopupCharging ||
                      !canManageBilling ||
                      !hasPlanChanges ||
                      isPendingCancellation ||
                      isPastDue
                    }
                    className="inline-flex cursor-pointer items-center justify-center rounded-lg !bg-[#f3f4f6] px-4 py-2 text-xs font-medium leading-4 !text-[#a4aab7] !border-0 !shadow-none transition enabled:!bg-[#1f2633] enabled:!text-white enabled:hover:!bg-black disabled:cursor-not-allowed"
                  >
                    {isLoading ? "Updating..." : "Checkout →"}
                  </button>
                </Tooltip>
              )}
            </div>
          </div>
        )}
        {modalView === "topup" && (
          // In-place body swap — same antd <Modal> instance, no nested
          // Modal/overlay (would flicker). Back returns to plan view; the
          // × close button hard-closes the whole modal. After a successful
          // charge TopUpView calls onClose() which fully shuts the modal;
          // afterClose resets us back to the manage view for next open.
          <TopUpView
            onBack={() => setModalView("manage")}
            onClose={() => onClose()}
          />
        )}
        {modalView === "confirm" && (
          <div className="flex flex-col bg-white">
            <div className="flex items-center gap-1.5 border-b border-[#e8eaed] px-6 py-4">
              <h3 className="!m-0 flex-1 text-[15px] font-semibold leading-5 tracking-[-0.3px] text-[#1f2633]">
                Confirm plan update
              </h3>
              <button
                type="button"
                onClick={() => onClose()}
                disabled={isLoading}
                className="flex cursor-pointer items-center justify-center rounded p-1 border-0 bg-transparent text-[#6c7689] transition hover:bg-[#f3f4f6] disabled:cursor-not-allowed disabled:opacity-50"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M12 4 4 12M4 4l8 8" />
                </svg>
              </button>
            </div>
            <div className="space-y-4 px-6 pb-4 pt-5">
              {confirmSnapshot && (
                <div className="space-y-2 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-700">
                  {confirmSnapshot.builderSeatDelta !== 0 ? (
                    <div className="flex items-center justify-between gap-4">
                      <span>
                        {confirmSnapshot.builderSeatDelta > 0 ? "+" : ""}
                        {confirmSnapshot.builderSeatDelta} AI Builder{" "}
                        {Math.abs(confirmSnapshot.builderSeatDelta) === 1
                          ? "License"
                          : "Licenses"}
                      </span>
                      <span>
                        {confirmSnapshot.builderSeatDeltaPrice > 0 ? "+" : "−"}
                        {formatUsd(
                          Math.abs(confirmSnapshot.builderSeatDeltaPrice),
                        )}
                        {confirmSnapshot.intervalSuffix}
                      </span>
                    </div>
                  ) : null}
                  {confirmSnapshot.deployedAppDelta !== 0 ? (
                    <div className="flex items-center justify-between gap-4">
                      <span>
                        {confirmSnapshot.deployedAppDelta > 0 ? "+" : ""}
                        {confirmSnapshot.deployedAppDelta} Deployed App
                        {Math.abs(confirmSnapshot.deployedAppDelta) === 1
                          ? ""
                          : "s"}
                      </span>
                      <span>
                        {confirmSnapshot.deployedAppDeltaPrice > 0 ? "+" : "−"}
                        {formatUsd(
                          Math.abs(confirmSnapshot.deployedAppDeltaPrice),
                        )}
                        {confirmSnapshot.intervalSuffix}
                      </span>
                    </div>
                  ) : null}
                  {confirmSnapshot.creditsChanged ? (
                    <div className="flex items-center justify-between gap-4">
                      <span>
                        AI Credits:{" "}
                        {confirmSnapshot.currentPlanCredits > 0
                          ? `${confirmSnapshot.currentPlanCredits.toLocaleString()} → `
                          : ""}
                        {confirmSnapshot.selectedPackCredits.toLocaleString()}
                      </span>
                      <span>
                        {confirmSnapshot.creditsDeltaPrice >= 0 ? "+" : "−"}
                        {formatUsd(Math.abs(confirmSnapshot.creditsDeltaPrice))}
                        {confirmSnapshot.intervalSuffix}
                      </span>
                    </div>
                  ) : null}
                  <div className="mt-2 border-t border-gray-200 pt-2 font-semibold text-gray-900">
                    <div className="flex items-center justify-between gap-4">
                      <span>Total change in cost</span>
                      <span>
                        {confirmSnapshot.totalAdditionalPrice > 0
                          ? "+"
                          : confirmSnapshot.totalAdditionalPrice < 0
                            ? "−"
                            : ""}
                        {formatUsd(
                          Math.abs(confirmSnapshot.totalAdditionalPrice),
                        )}
                        {confirmSnapshot.intervalSuffix}
                      </span>
                    </div>
                  </div>
                  {confirmSnapshot.hasImmediateCharges ? (
                    <p className="m-0 mt-2 text-xs font-normal text-gray-400">
                      {confirmSnapshot.proratedLoading
                        ? "Calculating prorated charge…"
                        : confirmSnapshot.proratedAmount != null
                          ? `An additional prorated charge of ${formatUsd(confirmSnapshot.proratedAmount, 2)} will be applied immediately for the remainder of your current billing period.`
                          : "An additional prorated charge will be applied immediately for the remainder of your current billing period."}
                    </p>
                  ) : null}
                  {confirmSnapshot.hasDowngradeChanges ? (
                    <p className="m-0 mt-2 text-xs font-normal text-gray-400">
                      This change will take effect at the start of your next
                      billing period.
                    </p>
                  ) : null}
                  {confirmSnapshot.isDowngradeBelowUsage ? (
                    <p className="m-0 mt-2 text-xs font-normal text-amber-600">
                      You&apos;ve used{" "}
                      {Math.ceil(
                        confirmSnapshot.currentUsageCredits,
                      ).toLocaleString()}{" "}
                      credits this period, which exceeds the{" "}
                      {confirmSnapshot.selectedPackCredits.toLocaleString()}{" "}
                      credit pack. No additional credits will be available until
                      the next billing cycle.
                    </p>
                  ) : null}
                </div>
              )}
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setModalView("manage")}
                  className="inline-flex h-9 cursor-pointer items-center justify-center rounded-md border border-gray-200 bg-white px-4 text-sm font-medium text-gray-900 shadow-[0_1px_2px_rgba(0,0,0,0.05)] transition hover:bg-gray-50"
                >
                  Back
                </button>
                <button
                  type="button"
                  data-test="manage-plan-confirm-btn"
                  onClick={() => {
                    performCheckout();
                  }}
                  disabled={isLoading}
                  className="inline-flex h-9 cursor-pointer items-center justify-center rounded-md bg-black px-4 text-sm font-medium !text-white shadow-[0_1px_2px_rgba(0,0,0,0.05)] transition hover:bg-black/90 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {isLoading ? "Updating..." : "Confirm and continue"}
                </button>
              </div>
            </div>
          </div>
        )}
        {modalView === "cancel" && (
          <div className="flex flex-col bg-white">
            <div className="flex items-center gap-1.5 border-b border-[#e8eaed] px-6 py-4">
              <h3 className="!m-0 flex-1 text-[15px] font-semibold leading-5 tracking-[-0.3px] text-[#1f2633]">
                Are you sure you want to cancel?
              </h3>
              <button
                type="button"
                onClick={() => onClose()}
                className="flex cursor-pointer items-center justify-center rounded p-1 border-0 bg-transparent text-[#6c7689] transition hover:bg-[#f3f4f6]"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M12 4 4 12M4 4l8 8" />
                </svg>
              </button>
            </div>
            <div className="px-6 pb-6 pt-5">
              <p className="!m-0 text-[13px] leading-5 text-[#454d5f]">
                If you cancel
                {resetDate
                  ? `, on ${resetDate}`
                  : " at the end of your billing period"}
                :
              </p>
              <div className="mt-3 space-y-1.5 text-[13px] font-medium leading-5 text-[#1f2633]">
                <div className="flex items-start gap-2">
                  <svg
                    aria-hidden="true"
                    className="mt-0.5 h-4 w-4 shrink-0"
                    viewBox="0 0 16 16"
                    fill="none"
                    stroke="#ef4444"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M4 4l8 8M12 4l-8 8" />
                  </svg>
                  All AI Builders lose ability to build or edit Applications
                </div>
                <div className="flex items-start gap-2">
                  <svg
                    aria-hidden="true"
                    className="mt-0.5 h-4 w-4 shrink-0"
                    viewBox="0 0 16 16"
                    fill="none"
                    stroke="#ef4444"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M4 4l8 8M12 4l-8 8" />
                  </svg>
                  Lose access to Clark AI, integrations and security settings
                </div>
                <div className="flex items-start gap-2">
                  <svg
                    aria-hidden="true"
                    className="mt-0.5 h-4 w-4 shrink-0"
                    viewBox="0 0 16 16"
                    fill="none"
                    stroke="#ef4444"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M4 4l8 8M12 4l-8 8" />
                  </svg>
                  All Deployed Apps will be taken offline
                </div>
                <div className="flex items-start gap-2">
                  <svg
                    aria-hidden="true"
                    className="mt-0.5 h-4 w-4 shrink-0"
                    viewBox="0 0 16 16"
                    fill="none"
                    stroke="#ef4444"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M4 4l8 8M12 4l-8 8" />
                  </svg>
                  Any Unused AI Credits will be forfeited
                </div>
              </div>

              <div className="mt-4 rounded border border-[rgba(255,159,53,0.24)] bg-[rgba(255,159,53,0.04)] p-2 text-xs leading-4 text-[#454d5f]">
                Your plan will remain active
                {resetDate ? (
                  <>
                    {" "}
                    until <span className="font-medium">{resetDate}</span>
                  </>
                ) : (
                  " until the end of your billing period"
                )}
                .
              </div>
            </div>
            <div className="flex justify-end gap-2 border-t border-[#e8eaed] px-6 py-4">
              <button
                type="button"
                onClick={(e) => {
                  setModalView("manage");
                  handleOpenPortalSession("subscription_cancel", e);
                }}
                className="inline-flex h-9 cursor-pointer items-center justify-center rounded-md border border-[#e8eaed] bg-white px-4 text-sm font-medium text-[#1f2633] shadow-[0_1px_2px_rgba(0,0,0,0.05)] transition hover:bg-[#f9fafb]"
              >
                Cancel plan
              </button>
              <button
                type="button"
                onClick={() => setModalView("manage")}
                className="inline-flex h-9 cursor-pointer items-center justify-center rounded-md bg-[#1f2633] px-4 text-sm font-medium !text-white shadow-[0_1px_2px_rgba(0,0,0,0.05)] transition hover:bg-[#1f2633]/90"
              >
                Keep my plan
              </button>
            </div>
          </div>
        )}
      </Modal>
    </>
  );
}
